import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import {
  formatEur,
  formatDateShort,
  MISSION_STATUS_LABEL,
  MISSION_STATUS_COLOR,
  MISSION_TYPE_LABEL,
} from '@/lib/format'

interface Mission {
  id: string
  title: string
  type: string
  status: string
  date: string
  city: string
  amount: number
  _count: { assignments: number }
}

function StatCard({
  label,
  value,
  icon,
  color = 'default',
}: {
  label: string
  value: string | number
  icon: string
  color?: 'default' | 'accent' | 'success' | 'warning'
}) {
  const accent = {
    default: 'text-text-primary',
    accent: 'text-[#8B9AFF]',
    success: 'text-success',
    warning: 'text-warning',
  }[color]

  return (
    <div className="glass-card p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-2xl">{icon}</span>
      </div>
      <p className={`text-3xl font-bold ${accent} mb-1`}>{value}</p>
      <p className="text-text-muted text-sm">{label}</p>
    </div>
  )
}

function MissionRow({ mission }: { mission: Mission }) {
  return (
    <Link
      to={`/missions/${mission.id}`}
      className="glass-card-hover flex items-center gap-4 p-4 group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-text-primary text-sm font-medium truncate">{mission.title}</p>
        <p className="text-text-muted text-xs mt-0.5">
          {mission.city} · {formatDateShort(mission.date)}
        </p>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-xs px-2 py-0.5 rounded-full border bg-white/[0.06] border-white/[0.12] text-text-secondary">
          {MISSION_TYPE_LABEL[mission.type] ?? mission.type}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${MISSION_STATUS_COLOR[mission.status]}`}>
          {MISSION_STATUS_LABEL[mission.status] ?? mission.status}
        </span>
        <span className="text-text-primary text-sm font-medium w-20 text-right">
          {formatEur(mission.amount)}
        </span>
        <span className="text-text-muted text-xs">
          {mission._count.assignments} candidature{mission._count.assignments !== 1 ? 's' : ''}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-text-muted group-hover:text-text-secondary transition-colors"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </div>
    </Link>
  )
}

export default function DashboardPage() {
  const [missions, setMissions] = useState<Mission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    apiFetch<Mission[]>('/companies/me/missions')
      .then(setMissions)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  const stats = {
    total: missions.length,
    open: missions.filter((m) => m.status === 'OPEN').length,
    completed: missions.filter((m) => m.status === 'COMPLETED').length,
    budget: missions.reduce((s, m) => s + m.amount, 0),
  }

  const recent = missions.slice(0, 6)

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Dashboard</h1>
        <p className="text-text-secondary mt-1 text-sm">Vue d'ensemble de votre activité</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total missions" value={stats.total} icon="📋" />
        <StatCard label="Missions ouvertes" value={stats.open} icon="🟢" color="success" />
        <StatCard label="Terminées" value={stats.completed} icon="✅" />
        <StatCard
          label="Budget engagé"
          value={formatEur(stats.budget)}
          icon="💰"
          color="accent"
        />
      </div>

      {/* Quick action */}
      <div className="mb-8 flex items-center gap-4">
        <Link to="/missions/nouvelle" className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl inline-flex items-center gap-2">
          <span>+</span> Publier une mission
        </Link>
        <Link to="/missions" className="glass-btn px-5 py-2.5 text-sm rounded-xl">
          Voir toutes les missions
        </Link>
      </div>

      {/* Recent missions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-text-primary">Missions récentes</h2>
          <Link to="/missions" className="text-xs text-text-muted hover:text-text-secondary transition-colors">
            Voir tout →
          </Link>
        </div>

        {error && (
          <div className="glass-card p-4 border-danger/25 bg-danger/10 text-danger text-sm mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card h-16 animate-pulse" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-4xl mb-4">📭</p>
            <p className="text-text-primary font-medium mb-2">Aucune mission pour l'instant</p>
            <p className="text-text-secondary text-sm mb-6">
              Publiez votre première mission pour trouver des prestataires.
            </p>
            <Link to="/missions/nouvelle" className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block">
              Créer ma première mission →
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {recent.map((m) => (
              <MissionRow key={m.id} mission={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
